import { useTranslation } from "react-i18next";
import type { EvidenceFocus } from "../../../../annotation";
import { mostSpecificClasses, TermLink, type EntityGroupShape } from "../../../../knowledgeData";
import { orderedLabels, termName, type TermCards } from "../../../../ontology";
import Chip from "../../../../../ui/feedback/Chip";
import { chipClassName } from "../../../../../ui/styles/chipStyles";
import { segmentDisplayName } from "../../documentNames";
import TextFocusButton from "../TextFocusButton";
import type { EntityCard, EntityGroup } from "./regestEntities";

interface EntityCardsProps {
  readonly groups: readonly EntityGroup<EntityGroupShape>[];
  /** "#n kinds" by statement key, as the statements' words carry it in the text. */
  readonly labelOf: (statementKey: string) => string;
  /** Whether a line is a numbered statement; one that isn't, such as a relation, reads as plain words, not a chip. */
  readonly numbered: (statementKey: string) => boolean;
  readonly focus: EvidenceFocus;
  /** The terms of the data model version the statements were written against, which name the classes. */
  readonly terms: { readonly commit: string | null; readonly cards: TermCards | null };
}

/**
 * Daniel's "Personen, Orte, Pfründen" (`render_entities`), after the text: per group of the project's
 * profile, a card for each entity with its words in the text and the statements about it. Each
 * follows into the text as the statement's own line does. No entities, no section.
 */
export default function EntityCards({ groups, labelOf, numbered, focus, terms }: EntityCardsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  if (groups.length === 0) return null;
  const titleOf = (shape: EntityGroupShape | null) => {
    if (shape === null) return t("sources.document.entities.other");
    const first = shape.classes[0];
    return orderedLabels(shape.title, language)[0]?.value ?? (first === undefined ? "" : termName(first, terms.cards, language));
  };
  const titles = groups.map((group) => titleOf(group.shape));
  return (
    <section id="regest-entities" aria-label={titles.join(", ")} className="space-y-3 py-3">
      {groups.map((group, index) => (
        <div key={group.shape?.classes.join(" ") ?? "other"} className="space-y-1">
          <h4 className="app-label text-xs">{titles[index]}</h4>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,22rem),1fr))] gap-2">
            {group.cards.map((card) => <EntityCardView key={card.individual.iri} card={card} shape={group.shape} labelOf={labelOf} numbered={numbered} focus={focus} terms={terms} />)}
          </ul>
        </div>
      ))}
    </section>
  );
}

function EntityCardView({ card, shape, labelOf, numbered, focus, terms }: { card: EntityCard; shape: EntityGroupShape | null } & Omit<EntityCardsProps, "groups">) {
  const { t } = useTranslation();
  const kinds = mostSpecificClasses(card.individual.classes, (iri) => terms.cards?.get(iri)?.parents ?? []);
  // Its class is named where the group's title doesn't already say it.
  const kindShown = shape === null || shape.classes.length > 1 || kinds.some((iri) => iri !== shape.classes[0]);
  return (
    <li className="space-y-1 rounded border border-[var(--app-border)] px-3 py-2 text-sm">
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="app-title font-semibold">{card.name ?? t("sources.document.regest.unnamed")}</span>
        {kindShown ? kinds.map((iri) => <Chip key={iri}><TermLink iri={iri} commit={terms.commit} cards={terms.cards} /></Chip>) : null}
      </p>
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
        {card.mentions.length === 0 ? null : (
          <div className="contents">
            <dt className="app-text-muted">{t("sources.document.entities.mentions")}</dt>
            <dd className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5">
              {/* A mention spelled as the card's name says only where it stands (usability round 5: no repeated name). */}
              {card.mentions.map((mention) => {
                const spelled = mention.words !== card.name;
                const where = mention.segmentPath === null ? null : segmentDisplayName(mention.segmentPath);
                return (
                  <TextFocusButton key={mention.key} statementKey={mention.key} focus={focus} className="rounded px-0.5">
                    {spelled || where === null ? <q>{mention.words}</q> : null}
                    {where === null ? null : <span className="app-text-muted text-xs">{spelled ? ` ${where}` : where}</span>}
                  </TextFocusButton>
                );
              })}
            </dd>
          </div>
        )}
        {card.statements.length === 0 ? null : (
          <div className="contents">
            <dt className="app-text-muted">{t("sources.document.added.statements")}</dt>
            <dd className="flex min-w-0 flex-wrap gap-1">
              {/* Only numbered statements are chips, as on the text; a relation reads as its words (usability round 5). */}
              {card.statements.map((key) => (
                <TextFocusButton key={key} statementKey={key} focus={focus} className={numbered(key) ? chipClassName() : "app-text-muted rounded px-0.5 text-xs"}>{labelOf(key)}</TextFocusButton>
              ))}
            </dd>
          </div>
        )}
      </dl>
    </li>
  );
}
